import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmDialog from '../components/ConfirmDialog';
import { ThemeToggle } from '../contexts/ThemeContext';
import './school-home.css';
import './teacher-home.css';
import './school-join.css'; // for the .sj-billing toggle in change-plan modal

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* Inline SVG icons used by the header action buttons. Stroke colour
 * inherits from the button text so each variant tints correctly. */
const IconCancel = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
    <circle cx="12" cy="12" r="9" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const IconSwap = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: '-2px' }}>
    <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

// Tier catalogue used by the change-plan modal. Mirrors /school-join.
const PLANS = [
  { id: 'madrasa',   name: 'Madrasa',   monthly: 24.99, yearly: 199, blurb: 'School-day access · up to 300 students' },
  { id: 'academy',   name: 'Academy',   monthly: 59.99, yearly: 499, blurb: '24/7 access · up to 300 students' },
  { id: 'institute', name: 'Institute', monthly: 79.99, yearly: 699, blurb: '24/7 access · up to 600 students' },
];

// Stripe subscription → human-readable label + tone for the header pill.
const SUB_STATUS_LABEL = {
  active:             { label: 'Subscription active', tone: 'good' },
  trialing:           { label: 'On trial',            tone: 'good' },
  past_due:           { label: 'Past due',            tone: 'warn' },
  unpaid:             { label: 'Unpaid',              tone: 'bad'  },
  canceled:           { label: 'Cancelled',           tone: 'muted' },
  incomplete:         { label: 'Setting up',          tone: 'warn' },
  incomplete_expired: { label: 'Setup failed',        tone: 'bad'  },
  paused:             { label: 'Paused',              tone: 'muted' },
};

export default function TeacherHome() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [cap, setCap] = useState(35);
  // School-wide cap (null = unlimited / Network tier)
  const [schoolStats, setSchoolStats] = useState({
    tier: null, billingCycle: null, cap: null, totalStudents: 0,
    name: null, status: null, subscriptionStatus: null,
    cancelAtPeriodEnd: false, currentPeriodEnd: null,
    classCap: 30, totalClasses: 0,
  });
  // Pending dialog for cancel/resume confirmation
  const [pendingBillingAction, setPendingBillingAction] = useState(null); // 'cancel' | 'resume'
  // Bulk book assignment — selection set + book catalogue
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const bulkCheckboxRef = useRef(null);
  const [books, setBooks] = useState([]);
  const [assignBookId, setAssignBookId] = useState('');
  const [assignToast, setAssignToast] = useState('');
  // Custom book-picker dropdown state (mirrors the contact subject-select)
  const [bookPickerOpen, setBookPickerOpen] = useState(false);
  const bookPickerRef = useRef(null);
  useEffect(() => {
    if (!bookPickerOpen) return;
    const onDown = (e) => { if (bookPickerRef.current && !bookPickerRef.current.contains(e.target)) setBookPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [bookPickerOpen]);
  // Change-plan modal state
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [changePlanBilling, setChangePlanBilling] = useState('monthly');
  const [pendingTierSwitch, setPendingTierSwitch] = useState(null); // { tier, billing }
  const [activeClassId, setActiveClassId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Class create/edit form
  const [classForm, setClassForm] = useState({ open: false, editing: null, name: '' });

  // Student create form (per active class)
  const [studentForm, setStudentForm] = useState({ open: false, name: '', pin: '' });

  // "Show once" creds modal — used for both initial PIN + reset.
  const [credsToShare, setCredsToShare] = useState(null); // { name, pin, label }

  // Confirm dialogs
  const [pendingDeleteClass,   setPendingDeleteClass]   = useState(null);
  const [pendingDeleteStudent, setPendingDeleteStudent] = useState(null);
  const [pendingResetPin,      setPendingResetPin]      = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adeeb-token');
    if (!token) { navigate('/login?next=/teacher', { replace: true }); return; }
    try { setUser(JSON.parse(localStorage.getItem('adeeb-user') || 'null')); } catch { /* ignore */ }
    // Re-fetch from /me — localStorage's adeeb-user can be stale (e.g.
    // after a parent password login that overwrote it) which hides the
    // Cancel / Change-plan buttons even though the DB still has the
    // user as a school admin. Mirror the fresh result back to storage
    // so the navbar + other components pick it up too.
    axios.get('/api/auth/me', { headers: authHeaders() })
      .then((r) => {
        if (r?.data?.user) {
          setUser(r.data.user);
          try {
            const cached = JSON.parse(localStorage.getItem('adeeb-user') || 'null') || {};
            localStorage.setItem('adeeb-user', JSON.stringify({ ...cached, ...r.data.user }));
            window.dispatchEvent(new Event('adeeb-auth-change'));
          } catch { /* ignore */ }
        }
      })
      .catch(() => { /* network blip — non-fatal */ });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [cls, stu, bks] = await Promise.all([
        axios.get('/api/teacher/classes', { headers: authHeaders() }),
        axios.get('/api/teacher/students', { headers: authHeaders() }),
        axios.get('/api/teacher/books',    { headers: authHeaders() }),
      ]);
      const list = cls.data?.classes || [];
      setClasses(list);
      setStudents(Array.isArray(stu.data) ? stu.data : []);
      setBooks(Array.isArray(bks.data) ? bks.data : []);
      setCap(cls.data?.cap || 35);
      if (cls.data?.school) setSchoolStats(cls.data.school);
      // Auto-select first class for the right pane.
      setActiveClassId((prev) => prev || (list[0]?._id ?? null));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) { navigate('/login?next=/teacher', { replace: true }); return; }
      if (status === 403) setErr('This account is not a teacher account.');
      else setErr(e?.response?.data?.message || 'Could not load.');
    } finally {
      setLoading(false);
    }
  };

  const total = students.length;
  const activeClass = classes.find((c) => c._id === activeClassId) || null;
  const studentsHere = useMemo(
    () => students.filter((s) => String(s.classId) === String(activeClassId)),
    [students, activeClassId]
  );

  const logout = async () => {
    try { const r = await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }); if (!r.ok && r.status !== 401) return; } catch { return; }
    localStorage.removeItem('adeeb-token');
    localStorage.removeItem('adeeb-user');
    localStorage.removeItem('adeeb-active-profile');
    localStorage.removeItem('adeeb-session-mode');
    window.dispatchEvent(new Event('adeeb-auth-change'));
    navigate('/', { replace: true });
  };

  /* Classes */
  const startCreateClass = () => setClassForm({ open: true, editing: null, name: '' });
  const startEditClass   = (c) => setClassForm({ open: true, editing: c, name: c.name });
  const cancelClassForm  = () => setClassForm({ open: false, editing: null, name: '' });
  const saveClass = async () => {
    if (busy) return;
    const name = classForm.name.trim();
    if (!name) { setErr('Class name is required.'); return; }
    setBusy(true); setErr('');
    try {
      if (classForm.editing) {
        await axios.patch(`/api/teacher/classes/${classForm.editing._id}`, { name }, { headers: authHeaders() });
      } else {
        const { data } = await axios.post('/api/teacher/classes', { name }, { headers: authHeaders() });
        setActiveClassId(data._id);
      }
      cancelClassForm();
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };
  const confirmDeleteClass = async () => {
    if (!pendingDeleteClass) return;
    setBusy(true);
    try {
      await axios.delete(`/api/teacher/classes/${pendingDeleteClass._id}`, { headers: authHeaders() });
      if (activeClassId === pendingDeleteClass._id) setActiveClassId(null);
      setPendingDeleteClass(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  /* Students */
  const startCreateStudent = () => {
    if (!activeClassId) { setErr('Create a class first.'); return; }
    setStudentForm({ open: true, name: '', pin: '' });
  };
  const cancelStudentForm = () => setStudentForm({ open: false, name: '', pin: '' });
  const saveStudent = async () => {
    if (busy) return;
    const name = studentForm.name.trim();
    const pin  = studentForm.pin.trim();
    if (!name) { setErr('Student name is required.'); return; }
    if (pin && !/^\d{4}$/.test(pin)) { setErr('PIN must be exactly 4 digits.'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post('/api/teacher/students', {
        name, classId: activeClassId, pin: pin || undefined,
      }, { headers: authHeaders() });
      cancelStudentForm();
      await load();
      setCredsToShare({ name: data.name, pin: data.initialPin, label: 'Student profile created' });
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };
  /* Billing — cancel-at-period-end + resume. School admins only. */
  const billingFmt = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const changeTier = (tierId) => {
    if (busy) return;
    setPendingTierSwitch({ tier: tierId, billing: changePlanBilling });
  };
  const confirmTierSwitch = async () => {
    if (!pendingTierSwitch || busy) return;
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post('/api/school/billing/change-tier',
        { tier: pendingTierSwitch.tier, billing: pendingTierSwitch.billing },
        { headers: authHeaders() });
      setSchoolStats((s) => ({
        ...s,
        tier: data.tier,
        billingCycle: data.billingCycle,
        subscriptionStatus: data.subscriptionStatus,
        currentPeriodEnd: data.currentPeriodEnd,
      }));
      setPendingTierSwitch(null);
      setChangePlanOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not switch plan.');
      setPendingTierSwitch(null);
    } finally { setBusy(false); }
  };
  const confirmBillingAction = async () => {
    const action = pendingBillingAction;
    if (!action) return;
    setBusy(true); setErr('');
    try {
      const url = action === 'cancel' ? '/api/school/billing/cancel' : '/api/school/billing/resume';
      const { data } = await axios.post(url, {}, { headers: authHeaders() });
      setSchoolStats((s) => ({
        ...s,
        cancelAtPeriodEnd: !!data.cancelAtPeriodEnd,
        currentPeriodEnd: data.currentPeriodEnd,
      }));
      setPendingBillingAction(null);
    } catch (e) {
      setErr(e?.response?.data?.message || `Could not ${action}.`);
    } finally { setBusy(false); }
  };

  const showStudentPin = async (s) => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const { data } = await axios.get(`/api/teacher/students/${s._id}/pin`, { headers: authHeaders() });
      setCredsToShare({ name: data.name, pin: data.pin, label: `${s.name}'s PIN` });
    } catch (e) {
      // Older students created before the plaintext PIN was stored will
      // hit this — the fallback is to just rotate.
      const code = e?.response?.data?.code;
      if (code === 'PIN_NOT_STORED') {
        if (window.confirm(`We don't have ${s.name}'s PIN on record. Generate a new one?`)) {
          await resetStudentPin(s);
        }
      } else {
        setErr(e?.response?.data?.message || 'Could not load PIN.');
      }
    } finally {
      setBusy(false);
    }
  };
  const resetStudentPin = (s) => { if (!busy) setPendingResetPin(s); };
  const confirmResetPin = async () => {
    const s = pendingResetPin;
    if (!s || busy) return;
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`/api/teacher/students/${s._id}/reset-pin`, {}, { headers: authHeaders() });
      setPendingResetPin(null);
      setCredsToShare({ name: s.name, pin: data.initialPin, label: 'New PIN' });
    } catch (e) {
      setErr(e?.response?.data?.message || 'Reset failed.');
    } finally { setBusy(false); }
  };
  const confirmDeleteStudent = async () => {
    if (!pendingDeleteStudent) return;
    setBusy(true);
    try {
      await axios.delete(`/api/teacher/students/${pendingDeleteStudent._id}`, { headers: authHeaders() });
      setPendingDeleteStudent(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  /* Selection + bulk assignment */
  const toggleSelected = (id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allInClassSelected = useMemo(() =>
    studentsHere.length > 0 && studentsHere.every((s) => selectedStudentIds.has(s._id))
  , [studentsHere, selectedStudentIds]);
  const toggleSelectAllInClass = () => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (allInClassSelected) {
        for (const s of studentsHere) next.delete(s._id);
      } else {
        for (const s of studentsHere) next.add(s._id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedStudentIds(new Set());

  // Reset selection when the active class changes — we only act on
  // the visible roster.
  useEffect(() => { setSelectedStudentIds(new Set()); }, [activeClassId]);

  const assignBook = async () => {
    if (!assignBookId)                         { setErr('Pick a book first.'); return; }
    if (selectedStudentIds.size === 0)         { setErr('Select at least one student.'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post('/api/teacher/students/assign-book', {
        bookId: assignBookId,
        profileIds: Array.from(selectedStudentIds),
      }, { headers: authHeaders() });
      setAssignToast(`Assigned "${data.bookTitle}" to ${data.modified} student${data.modified === 1 ? '' : 's'}.`);
      setTimeout(() => setAssignToast(''), 3000);
      // Reset selection + book picker so the bar returns to its idle
      // state — otherwise the same students stay highlighted as if
      // they're queued for another assignment.
      setSelectedStudentIds(new Set());
      setAssignBookId('');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not assign book.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="sh-page">
        <div className="sh-loading"><div className="sh-spinner" /><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="sh-page">
      <ThemeToggle className="theme-toggle--inline sh-theme-toggle" />

      <div className="sh-shell" style={{ maxWidth: 1080 }}>
        <header className="th-head">
          <div className="th-head-top">
            <div>
              <div className="sh-eyebrow">Teacher</div>
              <h1 className="sh-title" style={{ marginBottom: 4 }}>Hi {user?.name?.split(' ')[0] || 'there'}</h1>
              <div className="th-head-email">{user?.email || '—'}</div>
            </div>
            <div className="th-head-actions">
              {schoolStats.subscriptionStatus && !schoolStats.cancelAtPeriodEnd && (
                <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm th-head-btn-swap" onClick={() => { setChangePlanBilling(schoolStats.billingCycle || 'monthly'); setChangePlanOpen(true); }}>
                  <IconSwap /><span>Change plan</span>
                </button>
              )}
              {schoolStats.subscriptionStatus && (
                schoolStats.cancelAtPeriodEnd ? (
                  <button type="button" className="sh-btn sh-btn-primary sh-btn-sm" onClick={() => setPendingBillingAction('resume')}>
                    <IconRefresh />Resume subscription
                  </button>
                ) : (
                  <button type="button" className="sh-btn sh-btn-danger sh-btn-sm" onClick={() => setPendingBillingAction('cancel')}>
                    <IconCancel />Cancel subscription
                  </button>
                )
              )}
              <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={logout}>
                <IconLogout />Log out
              </button>
            </div>
          </div>

          <div className="th-head-stats">
            <span className={`th-stat${schoolStats.cap != null && schoolStats.totalStudents >= schoolStats.cap ? ' is-full' : ''}`}>
              <span className="th-stat-num">{schoolStats.totalStudents}</span>
              <span className="th-stat-sep">/</span>
              <span className="th-stat-den">{schoolStats.cap == null ? '∞' : schoolStats.cap}</span>
              <span className="th-stat-label">students</span>
            </span>
            <span className={`th-stat${schoolStats.totalClasses >= schoolStats.classCap ? ' is-full' : ''}`}>
              <span className="th-stat-num">{schoolStats.totalClasses}</span>
              <span className="th-stat-sep">/</span>
              <span className="th-stat-den">{schoolStats.classCap}</span>
              <span className="th-stat-label">classes</span>
            </span>
            {schoolStats.subscriptionStatus && (() => {
              const sub = SUB_STATUS_LABEL[schoolStats.subscriptionStatus] ||
                          { label: schoolStats.subscriptionStatus, tone: 'muted' };
              const tone = schoolStats.cancelAtPeriodEnd ? 'warn' : sub.tone;
              const label = schoolStats.cancelAtPeriodEnd
                ? `Ends ${billingFmt(schoolStats.currentPeriodEnd)}`
                : sub.label;
              return (
                <span className={`admin-pill admin-pill-${tone}`} title="Stripe subscription status">
                  {label}
                  {!schoolStats.cancelAtPeriodEnd && schoolStats.tier && (
                    <span className="th-stat-sub"> · {schoolStats.tier} {schoolStats.billingCycle || 'monthly'}</span>
                  )}
                </span>
              );
            })()}
          </div>
        </header>

        {err && <div className="sh-error">{err}</div>}

        <div className="th-grid">
          {/* Classes column */}
          <aside className="th-classes">
            <div className="sh-card-head" style={{ marginBottom: 12 }}>
              <h2 style={{ fontSize: '1.1rem', margin: 0 }}>Classes</h2>
              {!classForm.open && (
                <button type="button" className="sh-btn sh-btn-primary sh-btn-sm" onClick={startCreateClass}>+ Add</button>
              )}
            </div>

            {classForm.open && (
              <div className="th-class-form">
                <input
                  className="sh-input"
                  placeholder={classForm.editing ? 'Class name' : 'New class name'}
                  value={classForm.name}
                  onChange={(e) => setClassForm((f) => ({ ...f, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveClass(); if (e.key === 'Escape') cancelClassForm(); }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="sh-btn sh-btn-primary sh-btn-sm" onClick={saveClass} disabled={busy}>
                    {busy ? '…' : 'Save'}
                  </button>
                  <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={cancelClassForm}>Cancel</button>
                </div>
              </div>
            )}

            {classes.length === 0 ? (
              <p className="sh-empty" style={{ padding: 14 }}>No classes yet — add one to get started.</p>
            ) : (
              <ul className="th-class-list">
                {classes.map((c) => (
                  <li
                    key={c._id}
                    className={`th-class${activeClassId === c._id ? ' is-active' : ''}`}
                    onClick={() => setActiveClassId(c._id)}
                  >
                    <div className="th-class-name">{c.name}</div>
                    <div className="th-class-count">{c.studentCount} student{c.studentCount === 1 ? '' : 's'}</div>
                    <div className="th-class-actions">
                      <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={(e) => { e.stopPropagation(); startEditClass(c); }}>Edit</button>
                      <button type="button" className="sh-btn sh-btn-danger sh-btn-sm" onClick={(e) => { e.stopPropagation(); setPendingDeleteClass(c); }}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Students column */}
          <section className="th-students sh-card">
            {!activeClass ? (
              <p className="sh-empty">Select or create a class to manage its students.</p>
            ) : (
              <>
                <div className="sh-card-head">
                  <div>
                    <h2 style={{ margin: 0 }}>{activeClass.name}</h2>
                    <p className="th-class-meta">
                      {studentsHere.length} student{studentsHere.length === 1 ? '' : 's'} in this class
                    </p>
                  </div>
                  {!studentForm.open && (() => {
                    const atCap = schoolStats.cap != null && schoolStats.totalStudents >= schoolStats.cap;
                    return (
                      <button
                        type="button"
                        className="sh-btn sh-btn-primary"
                        onClick={startCreateStudent}
                        disabled={atCap}
                        title={atCap ? `Plan limit of ${schoolStats.cap} students reached — upgrade to add more.` : ''}
                      >
                        + Add student
                      </button>
                    );
                  })()}
                </div>

                {studentForm.open && (
                  <div className="sh-form">
                    <div className="sh-form-row" style={{ gridTemplateColumns: 'minmax(180px, 2fr) minmax(120px, 1fr)' }}>
                      <div className="sh-field">
                        <label className="sh-label">Student name</label>
                        <input
                          className="sh-input"
                          value={studentForm.name}
                          autoFocus
                          onChange={(e) => setStudentForm((f) => ({ ...f, name: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveStudent(); }}
                        />
                      </div>
                      <div className="sh-field">
                        <label className="sh-label">4-digit PIN (optional)</label>
                        <input
                          className="sh-input"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="Auto-generate"
                          value={studentForm.pin}
                          onChange={(e) => setStudentForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))}
                        />
                      </div>
                    </div>
                    <div className="sh-form-actions">
                      <button type="button" className="sh-btn sh-btn-primary" onClick={saveStudent} disabled={busy}>
                        {busy ? 'Creating…' : 'Create student'}
                      </button>
                      <button type="button" className="sh-btn sh-btn-ghost" onClick={cancelStudentForm}>Cancel</button>
                    </div>
                  </div>
                )}

                {studentsHere.length === 0 ? (
                  <p className="sh-empty">No students in this class yet.</p>
                ) : (
                  <>
                    <div className="th-bulk-bar">
                      <label className="th-bulk-checkbox">
                        <input
                          ref={(el) => {
                            bulkCheckboxRef.current = el;
                            if (el) {
                              const partial = !allInClassSelected
                                && studentsHere.some((s) => selectedStudentIds.has(s._id));
                              el.indeterminate = partial;
                            }
                          }}
                          type="checkbox"
                          checked={allInClassSelected}
                          onChange={toggleSelectAllInClass}
                          aria-label="Select all students in this class"
                        />
                        <span>{selectedStudentIds.size > 0 ? `${selectedStudentIds.size} selected` : 'Select all'}</span>
                      </label>
                      {(() => {
                        const disabled = selectedStudentIds.size === 0;
                        const selected = books.find((b) => b._id === assignBookId);
                        return (
                          <div className={`th-bookpicker${disabled ? ' is-disabled' : ''}`} ref={bookPickerRef}>
                            <button
                              type="button"
                              className={`th-bookpicker-trigger${bookPickerOpen ? ' is-open' : ''}`}
                              onClick={() => !disabled && setBookPickerOpen((o) => !o)}
                              disabled={disabled}
                              aria-haspopup="listbox"
                              aria-expanded={bookPickerOpen}
                            >
                              <span className="th-bookpicker-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                </svg>
                              </span>
                              <span className="th-bookpicker-text">
                                <span className="th-bookpicker-label">{selected ? selected.title : 'Pick a book to assign'}</span>
                                <span className="th-bookpicker-hint">
                                  {selected
                                    ? (selected.titleAr || `${books.length} book${books.length === 1 ? '' : 's'} in your library`)
                                    : `${books.length} book${books.length === 1 ? '' : 's'} in your library`}
                                </span>
                              </span>
                              <span className="th-bookpicker-chevron" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </span>
                            </button>
                            {bookPickerOpen && (
                              <ul className="th-bookpicker-menu" role="listbox">
                                {books.length === 0 ? (
                                  <li className="th-bookpicker-empty">No books in your library yet.</li>
                                ) : books.map((b) => {
                                  const isSelected = b._id === assignBookId;
                                  return (
                                    <li key={b._id}>
                                      <button
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`th-bookpicker-option${isSelected ? ' is-selected' : ''}`}
                                        onClick={() => { setAssignBookId(b._id); setBookPickerOpen(false); }}
                                      >
                                        <span className="th-bookpicker-option-icon" aria-hidden="true">
                                          {b.coverImage
                                            ? <img src={b.coverImage} alt="" />
                                            : (
                                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                              </svg>
                                            )}
                                        </span>
                                        <span className="th-bookpicker-option-text">
                                          <span className="th-bookpicker-option-label">{b.title}</span>
                                          {b.titleAr && <span className="th-bookpicker-option-hint">{b.titleAr}</span>}
                                        </span>
                                        {isSelected && (
                                          <span className="th-bookpicker-option-check" aria-hidden="true">
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                              <polyline points="5 12 10 17 19 7" />
                                            </svg>
                                          </span>
                                        )}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })()}
                      <button
                        type="button"
                        className="sh-btn sh-btn-primary sh-btn-sm"
                        onClick={assignBook}
                        disabled={busy || !assignBookId || selectedStudentIds.size === 0}
                      >
                        Assign
                      </button>
                      {selectedStudentIds.size > 0 && (
                        <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={clearSelection}>Clear</button>
                      )}
                    </div>
                    {assignToast && <div className="th-toast">{assignToast}</div>}
                    <ul className="th-student-list">
                      {studentsHere.map((s) => {
                        const checked = selectedStudentIds.has(s._id);
                        const assignedCount = s.assignedBookIds?.length || 0;
                        return (
                          <li key={s._id} className={`th-student${checked ? ' is-selected' : ''}`}>
                            <label className="th-student-check">
                              <input type="checkbox" checked={checked} onChange={() => toggleSelected(s._id)} aria-label={`Select ${s.name}`} />
                            </label>
                            <div className="th-student-name">
                              {s.name}
                              {assignedCount > 0 && (
                                <span className="th-student-meta"> · {assignedCount} book{assignedCount === 1 ? '' : 's'} assigned</span>
                              )}
                            </div>
                            <div className="th-student-actions">
                              <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={() => showStudentPin(s)}>Show PIN</button>
                              <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={() => resetStudentPin(s)}>Reset PIN</button>
                              <button type="button" className="sh-btn sh-btn-danger sh-btn-sm" onClick={() => setPendingDeleteStudent(s)}>Delete</button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={!!pendingDeleteClass}
        tone="danger"
        title={pendingDeleteClass ? `Delete ${pendingDeleteClass.name}?` : ''}
        body="Every student in this class will also be deleted, along with their reading progress, favourites, quizzes and rewards. This can't be undone."
        confirmLabel="Delete class"
        cancelLabel="Keep class"
        busy={busy}
        onClose={() => { if (!busy) setPendingDeleteClass(null); }}
        onConfirm={confirmDeleteClass}
      />
      <ConfirmDialog
        open={!!pendingDeleteStudent}
        tone="danger"
        title={pendingDeleteStudent ? `Delete ${pendingDeleteStudent.name}?` : ''}
        body="Their reading progress, favourites, quizzes and rewards will be permanently removed."
        confirmLabel="Delete student"
        cancelLabel="Keep student"
        busy={busy}
        onClose={() => { if (!busy) setPendingDeleteStudent(null); }}
        onConfirm={confirmDeleteStudent}
      />
      <ConfirmDialog
        open={!!pendingResetPin}
        title={pendingResetPin ? `Reset ${pendingResetPin.name}'s PIN?` : ''}
        body="A new 4-digit PIN will be generated and shown to you once. The current PIN will stop working immediately, so make sure you can pass the new one to the student."
        confirmLabel="Reset PIN"
        cancelLabel="Keep current PIN"
        busy={busy}
        onClose={() => { if (!busy) setPendingResetPin(null); }}
        onConfirm={confirmResetPin}
      />
      <ConfirmDialog
        open={pendingBillingAction === 'cancel'}
        tone="danger"
        title="Cancel subscription?"
        body={`Your school keeps full access until ${billingFmt(schoolStats.currentPeriodEnd)}, then it ends and the school code stops working. No further charges will be made. You can resume any time before that date.`}
        confirmLabel="Yes, cancel at period end"
        cancelLabel="Keep subscription"
        busy={busy}
        onClose={() => { if (!busy) setPendingBillingAction(null); }}
        onConfirm={confirmBillingAction}
      />
      <ConfirmDialog
        open={pendingBillingAction === 'resume'}
        title="Resume subscription?"
        body={`Cancellation will be undone. The next charge will fire on ${billingFmt(schoolStats.currentPeriodEnd)} as normal.`}
        confirmLabel="Yes, keep it going"
        cancelLabel="Stay cancelled"
        busy={busy}
        onClose={() => { if (!busy) setPendingBillingAction(null); }}
        onConfirm={confirmBillingAction}
      />
      {pendingTierSwitch && (() => {
        const plan = PLANS.find((p) => p.id === pendingTierSwitch.tier);
        const isYearly = pendingTierSwitch.billing === 'yearly';
        const price  = plan ? (isYearly ? plan.yearly : plan.monthly) : null;
        const period = isYearly ? 'year' : 'month';
        const isUpgrade = (() => {
          const order = { madrasa: 0, academy: 1, institute: 2, network: 3 };
          return order[pendingTierSwitch.tier] > order[schoolStats.tier];
        })();
        return (
          <ConfirmDialog
            open
            tone={isUpgrade ? 'neutral' : 'danger'}
            title={`Switch to ${plan?.name || pendingTierSwitch.tier}?`}
            body={
              <>
                <strong>£{price}/{period}</strong> — Stripe will charge the prorated difference{' '}
                <strong>now</strong>, and every renewal will be at this new rate.
                {!isUpgrade && (
                  <>{' '}You're moving to a smaller plan; check your student count fits the new cap first.</>
                )}
              </>
            }
            confirmLabel={`Switch to ${plan?.name || 'plan'}`}
            cancelLabel="Keep current plan"
            busy={busy}
            onClose={() => { if (!busy) setPendingTierSwitch(null); }}
            onConfirm={confirmTierSwitch}
          />
        );
      })()}

      {changePlanOpen && (
        <div className="sh-creds-backdrop" onClick={() => !busy && setChangePlanOpen(false)}>
          <div className="sh-creds" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="sh-creds-eyebrow">Billing</div>
            <h3 className="sh-creds-title">Change your plan</h3>
            <p className="sh-creds-body">
              Stripe charges the prorated difference immediately and renewals continue at the new rate. You're currently on{' '}
              <strong>{schoolStats.tier || '—'} {schoolStats.billingCycle || ''}</strong>.
            </p>

            <div className="sj-billing" data-active={changePlanBilling} style={{ margin: '6px auto 18px' }}>
              <button type="button"
                className={`sj-billing-btn ${changePlanBilling === 'monthly' ? 'is-active' : ''}`}
                onClick={() => setChangePlanBilling('monthly')}>Monthly</button>
              <button type="button"
                className={`sj-billing-btn ${changePlanBilling === 'yearly' ? 'is-active' : ''}`}
                onClick={() => setChangePlanBilling('yearly')}>
                Yearly <span className="sj-billing-save">save ~30%</span>
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {PLANS.map((p) => {
                const isCurrent = schoolStats.tier === p.id && schoolStats.billingCycle === changePlanBilling;
                const price = changePlanBilling === 'yearly' ? p.yearly : p.monthly;
                const period = changePlanBilling === 'yearly' ? '/yr' : '/mo';
                return (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, padding: '14px 16px', borderRadius: 12,
                    border: `1px solid ${isCurrent ? 'rgba(240,197,96,0.7)' : 'rgba(184,134,11,0.3)'}`,
                    background: isCurrent ? 'rgba(184,134,11,0.16)' : 'rgba(13,10,5,0.4)',
                  }}>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ color: '#fff8e6', fontFamily: "'Cormorant Garamond','Georgia',serif", fontSize: '1.2rem', fontWeight: 600 }}>{p.name}</div>
                      <div style={{ color: 'rgba(245,230,200,0.6)', fontSize: '0.82rem', fontFamily: "'Inter',system-ui,sans-serif" }}>{p.blurb}</div>
                    </div>
                    <div style={{ color: '#f0c560', fontFamily: "'Inter',system-ui,sans-serif", fontWeight: 700, minWidth: 90, textAlign: 'right' }}>
                      £{price}<span style={{ color: 'rgba(245,230,200,0.6)', fontWeight: 400, fontSize: '0.78rem' }}>{period}</span>
                    </div>
                    {isCurrent ? (
                      <button type="button" disabled className="sh-btn sh-btn-ghost sh-btn-sm" style={{ minWidth: 90, cursor: 'default', opacity: 0.7 }}>Current</button>
                    ) : (
                      <button type="button" className="sh-btn sh-btn-primary sh-btn-sm" disabled={busy} onClick={() => changeTier(p.id)} style={{ minWidth: 90 }}>
                        {busy ? '…' : 'Switch'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {err && <div className="sh-error" style={{ marginTop: 14 }}>{err}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={() => !busy && setChangePlanOpen(false)} disabled={busy}>
                Close
              </button>
            </div>

            <p className="sh-creds-body" style={{ marginTop: 12, fontSize: '0.78rem' }}>
              Need <strong>Network</strong> tier (multi-campus, unlimited students)? <Link to="/contact" className="sh-link">Get in touch &rarr;</Link>
            </p>
          </div>
        </div>
      )}

      {credsToShare && (
        <div className="sh-creds-backdrop" onClick={() => setCredsToShare(null)}>
          <div className="sh-creds" onClick={(e) => e.stopPropagation()}>
            <div className="sh-creds-eyebrow">{credsToShare.label}</div>
            <h3 className="sh-creds-title">Share this PIN once</h3>
            <p className="sh-creds-body">
              Only shown <strong>this once</strong>. Note it down or pass it to {credsToShare.name} now.
            </p>
            <dl className="sh-creds-fields">
              <div><dt>Name</dt><dd><code>{credsToShare.name}</code></dd></div>
              <div><dt>PIN</dt><dd><code style={{ fontSize: '1.4rem', letterSpacing: 4 }}>{credsToShare.pin}</code></dd></div>
            </dl>
            <button type="button" className="sh-btn sh-btn-primary" onClick={() => setCredsToShare(null)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
